import React from "react";

import Button from "@common/components/Button/Button";
import { HelperText } from "@common/components/HelperText/HelperText";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import { ToggleSection } from "@common/components/ToggleSection/ToggleSection";
import { Workspace } from "@common/models";
import {
  forwardingOptions,
  getIntegrationTypeName,
  IntegrationLayoutEntry,
  IntegrationType,
} from "@common/models/integrations";
import { Linear } from "@common/models/integrations/Linear.model";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";

import { Form } from "../ModalBody.styles";
import { TeamOption, useLinearIntegrationFormConsumer } from "./consumers/useLinearIntegrationFormConsumer";

interface LinearIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Linear;
  selectedAppId: string;
  workspace: Workspace;
  onFinish: () => void;
}

export const LinearIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
}: LinearIntegrationModalProps) => {
  const {
    forwardingOption,
    teamOption,
    teamOptions,
    projectOption,
    projectOptions,
    handleFormChange,
    handleFormSubmit,
    disabled,
    loading,
    webhook,
    loadOptions,
    integrationLayout,
    moveIntegrationLayoutEntryDown,
    moveIntegrationLayoutEntryUp,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useLinearIntegrationFormConsumer({ selectedAppId, onFinish, integration });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(
        disabled,
        teamOption,
        projectOption,
        forwardingOption,
        webhook,
        integrationLayout,
        integration,
      ) && !hasMetadataKeyChanges;

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: "No changes have been made",
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.LINEAR}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.LINEAR)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.LINEAR)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="teamOption"
          value={teamOption}
          options={teamOptions}
          placeholder="Please choose"
          label="Team"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || loading}
          loadOptions={loadOptions}
        />

        <HelperText>
          Forwarded tickets are sent to this team’s Triage inbox. Disable triage for this team in Linear › Workspace
          settings › Team › Triage if you want the tickets to be sent to the Project you choose below.
        </HelperText>

        <SelectDropdown
          name="projectOption"
          value={projectOption}
          options={projectOptions}
          placeholder="Please choose"
          label="Project"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || !teamOption || loading}
        />
        <ToggleSection
          name="webhooks"
          title="Linear updates Shake"
          subtitle="Changing Linear ticket’s status will automatically update its Shake status. All other changes to Linear ticket will be listed in Shake ticket too."
          checked={webhook}
          onChange={() => handleFormChange("webhook", !webhook)}
          tooltipText={!integration?.is_admin ? "Please reauthenticate as an Admin to enable this feature" : undefined}
          disabled={disabled || !teamOption || loading || !integration?.is_admin}
        />

        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  disabled: boolean,
  teamOption: Option,
  projectOption: Option,
  forwardingOption: Option,
  webhook: boolean,
  integrationLayout: IntegrationLayoutEntry[],
  integration?: Linear,
) => {
  if (disabled) return true;
  if (!teamOption || !forwardingOption) return true;

  const parsedTeamOption = JSON.parse(teamOption.value) as TeamOption;
  if (
    integration &&
    (parsedTeamOption.teamId !== integration?.linear_team_id ||
      !!forwardingOption.value !== integration?.automatic_forward ||
      (integration.linear_project_id && projectOption.value !== integration?.linear_project_id) ||
      (!integration.linear_project_id && projectOption.value !== "{}") ||
      integration?.is_webhook_enabled !== webhook)
  ) {
    return false;
  }

  // Check if integration layout has changed
  return !(integration && hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout));
};

const hasIntegrationLayoutChanged = (
  currentLayout: IntegrationLayoutEntry[],
  originalLayout?: IntegrationLayoutEntry[],
): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
